import React, { useReducer, useCallback } from "react";
import { Container, Form, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Entry.css";

// ─── バリデーション定数 ────────────────────────────────────────────
const MAX_FILE_SIZE  = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES  = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9-]+$/;

// ─── 職種リスト ────────────────────────────────────────────────────
const JOB_OPTIONS = [
  "機械設計職",
  "電気設計職",
  "製造職",
  "開発機械設計職",
  "生産管理職",
];

// ─── 募集要項データ ────────────────────────────────────────────────
const REQUIREMENTS = [
  {
    heading: "募集要項",
    details: [
      { title: "職種",   text: JOB_OPTIONS.join("\n") },
      { title: "勤務地", text: "埼玉県戸田市氷川町2-17-27（転勤なし）" },
    ],
  },
  {
    heading: "応募条件",
    details: [
      { title: "最終学歴", text: "不問" },
      { title: "保有資格", text: "普通運転免許（AT限定可）" },
      { title: "年齢",     text: "18歳以上～45歳未満（長期勤続によるキャリア形成）" },
      { title: "経験",     text: "応募されたい職種の実務経験3年以上、ご経験がない場合のチャレンジ応募歓迎" },
      { title: "その他",   text: "長期就業における健康面向上の都合、喫煙経験のない方または禁煙を今後継続されている方を採用します" },
    ],
  },
  {
    heading: "選考フロー",
    details: [
      { title: "書類選考", text: "ご応募頂いてから1週間以内にメールまたはお電話で返答" },
      { title: "面接",     text: "原則1回、勤務地での対面にて実施（リモート面接は状況により検討可）" },
      { title: "内定通知", text: "諸条件を記載した内定通知書を発行、返答期限2週間以内" },
      { title: "その他",   text: "面接時間は約2時間を予定、工場見学あり、交通費用は応募者負担、筆記試験など無し" },
    ],
  },
  {
    heading: "諸条件",
    details: [
      { title: "給与",       text: "月給制、前職の年収を参考にご経験・能力を考慮し決定" },
      { title: "残業手当",   text: "1分単位積算、30分未満切捨、以上切上げ1時間単位支給（月平均15h程度）" },
      { title: "各種手当",   text: "家族手当、インフレ手当2万、禁煙手当1万" },
      { title: "出張手当",   text: "ホテル代実費支給、宿泊出張、日帰り出張、海外出張別途" },
      { title: "賞与",       text: "年2回支給（3月末・9月末）、額面は当期業績による" },
      { title: "通勤方法",   text: "通勤手当全額支給、マイカー通勤可（駐車場代月1千円負担あり）" },
      { title: "勤務時間",   text: "8:30〜17:30（休憩1時間）" },
      { title: "休日・休暇", text: "完全週休2日制（土日祝日、年末年始休暇、夏季休暇）、有給休暇、時間単位有給" },
      { title: "試用期間",   text: "原則3ヶ月" },
      { title: "各種保険",   text: "健康保険・厚生年金・雇用保険・労災保険" },
      { title: "福利厚生",   text: "24時間の傷害保険付与、リゾート施設利用可、仕出し弁当有り、チケットレストラン利用可" },
    ],
  },
];

// ─── フォーム state を useReducer で一元管理 ────────────────────────
const FORM_INITIAL = { name: "", phone: "", email: "", job: "", message: "", file: null };

function formReducer(state, action) {
  if (action.type === "SET_FIELD") {
    return { ...state, [action.field]: action.value };
  }
  if (action.type === "RESET") {
    return FORM_INITIAL;
  }
  return state;
}

export default function Entry() {
  const navigate = useNavigate();
  const [form,   dispatchForm] = useReducer(formReducer, FORM_INITIAL);
  const [status, setStatus]    = React.useState(null);

  // ─── 入力ハンドラ ─────────────────────────────────────────────
  const handleChange = useCallback((e) => {
    dispatchForm({ type: "SET_FIELD", field: e.target.name, value: e.target.value });
  }, []);

  const handleFileChange = useCallback((e) => {
    dispatchForm({ type: "SET_FIELD", field: "file", value: e.target.files[0] || null });
  }, []);

  // ─── クライアントサイドバリデーション ────────────────────────
  const validate = useCallback(() => {
    if (form.file) {
      if (form.file.size > MAX_FILE_SIZE)    return "ファイルサイズは5MBまでです";
      if (!ALLOWED_TYPES.includes(form.file.type)) return "PDFまたはWordファイルのみアップロード可能です";
    }
    if (!EMAIL_REGEX.test(form.email)) return "正しいメールアドレスを入力してください";
    if (!PHONE_REGEX.test(form.phone)) return "正しい電話番号を入力してください";
    return null;
  }, [form]);

  // ─── 送信ハンドラ ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    const error = validate();
    if (error) {
      setStatus({ type: "danger", message: error });
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (val !== null) formData.append(key, val);
    });

    try {
      const res  = await fetch("/send_mail_attach.php", { method: "POST", body: formData });
      const data = await res.json();

      if (data.status === "ok") {
        navigate("/thanks");
      } else {
        setStatus({ type: "danger", message: data.message || "送信に失敗しました" });
      }
    } catch {
      setStatus({ type: "danger", message: "通信エラーが発生しました" });
    }
  };

  return (
    <div className="entry-page">
      <div id="page-top" />

      {/* ── 募集要項・応募条件・諸条件 ────────────────────────────── */}
      <Container className="mb-5">
        {REQUIREMENTS.map((section) => (
          <div key={section.heading} className="section-block mb-4">
            <h2 className="section-heading">{section.heading}</h2>
            <ul className="info-list">
              {section.details.map((item) => (
                <li key={item.title}>
                  <span className="detail-title">{item.title}：</span>
                  <span className="detail-text">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Container>

      {/* ── エントリーフォーム ──────────────────────────────────── */}
      <section className="py-5">
        <Container>
          <h2 className="mb-4 text-center">エントリーフォーム</h2>

          {status && <Alert variant={status.type}>{status.message}</Alert>}

          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <Form.Group className="mb-3">
              <Form.Label>お名前</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>電話番号</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>メールアドレス</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>希望職種</Form.Label>
              <Form.Select
                name="job"
                value={form.job}
                onChange={handleChange}
                required
              >
                <option value="">選択してください</option>
                {JOB_OPTIONS.map((j) => (
                  <option key={j} value={j}>{j}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>志望動機・自己PR</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="message"
                value={form.message}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>履歴書・職務経歴書（PDF/DOC/DOCX、最大5MB）</Form.Label>
              <Form.Control
                type="file"
                name="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
              />
            </Form.Group>

            <div className="text-center">
              <Button type="submit" variant="primary" size="lg">
                応募する
              </Button>
            </div>
          </Form>
        </Container>
      </section>
    </div>
  );
}